<template>
  <div class="gas-cockpit">
    <!-- 内容舞台：占满视口高度，左右固定 30px 边距 -->
    <div ref="stage" class="gas-stage">

      <!-- ===== 顶部信息栏 ===== -->
      <header class="topbar panel">
        <div class="brand">
          <div class="logo">
            <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
              <path d="M24 4 41 14v20L24 44 7 34V14z" stroke="currentColor" stroke-width="2.4"/>
              <path d="M26 12 16 27h7l-2 10 11-16h-7z" fill="currentColor"/>
            </svg>
          </div>
          <div class="brand-txt">
            <h1>工位总览</h1>
            <p>STATION OVERVIEW</p>
          </div>
          <span class="mode-tag">{{ zone }}区 · 流程区</span>

          <!-- ✅ 关键字筛选：合同号 / 最终业主
               模糊匹配（包含即可、不区分大小写），与下方的型号、状态筛选是「并且」关系，
               可以叠加使用；命中的工位和下面筛选的效果一样（高亮 / 其余淡化） -->
          <span class="brand-filter">
            <label class="kw" :class="{ on: filterContract }">
              <span class="kw-label">合同号</span>
              <input
                v-model="filterContract"
                class="kw-input"
                type="text"
                placeholder="关键字"
                autocomplete="off"
                title="按合同号模糊筛选（与型号 / 状态筛选叠加）"
                @keydown.enter.prevent
              />
              <button v-if="filterContract" type="button" class="kw-clear" title="清除" @click="filterContract = ''">×</button>
            </label>
            <label class="kw" :class="{ on: filterOwner }">
              <span class="kw-label">最终业主</span>
              <input
                v-model="filterOwner"
                class="kw-input"
                type="text"
                placeholder="关键字"
                autocomplete="off"
                title="按最终业主模糊筛选（与型号 / 状态筛选叠加）"
                @keydown.enter.prevent
              />
              <button v-if="filterOwner" type="button" class="kw-clear" title="清除" @click="filterOwner = ''">×</button>
            </label>
          </span>
        </div>
        <!-- 核心指标 -->
        <div class="metrics">
          <div class="metric">
            <span class="m-label">工位总数</span>
            <span class="m-value"><b>{{ stats.total }}</b><small>台</small></span>
          </div>
          <div class="metric">
            <span class="m-label">在制工位</span>
            <span class="m-value c-cyan"><b>{{ stats.ok + stats.warn + stats.err }}</b></span>
          </div>
          <div class="metric">
            <span class="m-label">停工工位</span>
            <span class="m-value c-red"><b>{{ stats.err }}</b><small>个</small></span>
          </div>
          <div class="metric">
            <span class="m-label">本班完工</span>
            <span class="m-value c-green"><b>{{ doneTotal }}</b><small>台</small></span>
          </div>
        </div>



        <!-- 时钟 / 班次 -->
        <div class="clock-box">
          <div class="clock-time">{{ now }}</div>
          <div class="clock-sub"><span>{{ dateText }}</span><span class="shift">{{ shift }}</span></div>
        </div>

        <!-- 全屏 / 退出全屏：整个看板铺满屏幕（原生全屏，不支持时自动退回铺满视口） -->
        <button
          class="fs-btn"
          type="button"
          :title="fs ? '退出全屏（Esc）' : '全屏显示'"
          :aria-label="fs ? '退出全屏' : '全屏显示'"
          @click="toggleFs"
        >
          <svg v-if="!fs" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <!-- 返回主页：最右侧，仅一个实心蓝色小房子 -->
        <button class="back-btn" type="button" title="返回主页" @click="backHome">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M11.34 3.28a1 1 0 0 1 1.32 0l8.4 7.36a1 1 0 0 1-.66 1.75H19.4v7.4a1.4 1.4 0 0 1-1.4 1.4h-3.4v-5.6h-5.2v5.6H6a1.4 1.4 0 0 1-1.4-1.4v-7.4H3.6a1 1 0 0 1-.66-1.75z"
              fill="currentColor"
            />
          </svg>
        </button>
      </header>

      <!-- ===== A 区 · 流程区 看板：14 行 × 4 列桌子，同一列上下紧挨的 2 桌 = 一个下钻单元 ===== -->
      <main class="board panel">
        <div class="board-head">
          <div class="b-left">
            <span class="z-mark"></span>
            <h2>{{ zone }}区 · 流程区</h2>

          </div>
          <div class="b-right">
            <!-- 多选筛选提示：点亮的按钮就是筛选条件（可多选），命中工位高亮、其余淡化 -->
            <span class="hint" :class="{ on: filterActive, warn: filterActive && !filterCount }">{{ filterActive ? (filterCount ? `${filterText} · 命中 ${filterCount} 个工位` : `${filterText} · 当前条件没有命中的工位`) : '' }}</span>
            <!-- 接口失败提示 -->
            <span v-if="loadErr" class="hint" style="color: #ff9b9b;">{{ loadErr }}</span>
            <!-- SSE 断连提示（EventSource 会自动重连，恢复后自动消失） -->
            <span v-else-if="sseErr" class="hint" style="color: #ffe08a;">{{ sseErr }}</span>

            <!-- 各在制型号系列工位数：系列清单由当前页面实际出现的型号实时得出（有 LA301 / LA200 就显示它们，
                 本次没有 PA200 就不显示），多选：点几个筛几个 -->
            <span class="m-chips">
              <button
                v-for="m in modelStats"
                :key="m.series"
                type="button"
                class="chip c-md btn"
                :class="{ on: isModelOn(m.series) }"
                :title="`系列 ${m.series} · 在制 ${m.count} 个工位（可多选：点几个筛几个，再点取消）`"
                @click="toggleModel(m.series)"
              >{{ m.series }}<b>{{ m.count }}</b></button>
            </span>
            <!-- 各状态工位数：多选，点哪些高亮哪些 -->
            <button
              v-for="s in statusOptions"
              :key="s.key"
              type="button"
              class="chip btn"
              :class="[s.cls, { on: isStatusOn(s.key), zero: !s.count }]"
              :title="`${s.label}工位 ${s.count} 个（数量随选中产品汇总；可多选：点哪些高亮哪些，再点取消）`"
              @click="toggleStatus(s.key)"
            >{{ s.label }}<b>{{ s.count }}</b></button>
            <!-- 全部：一键清除筛选，回到全量显示 -->
            <button
              type="button"
              class="chip c-all btn"
              :class="{ on: !filterActive }"
              title="全部工位（点击清除筛选）"
              @click="clearFilter"
            >
              <svg class="i-all" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="2" />
                <rect x="13" y="3.5" width="7.5" height="7.5" rx="2" />
                <rect x="3.5" y="13" width="7.5" height="7.5" rx="2" />
                <rect x="13" y="13" width="7.5" height="7.5" rx="2" />
              </svg>全部<b>{{ stats.total }}</b>
            </button>
          </div>
        </div>

        <div class="board-body">
          <div v-for="(pr, i) in pairRows" :key="i" class="pair-row">
            <!-- 行号：每一横排最左侧标出该排两个物理行的编号（A.01 / A.02），与上下两张桌子一一对齐 -->
            <div class="row-axis">
              <span v-for="rc in pr[0].rowCodes" :key="rc">{{ rc }}</span>
            </div>
            <!-- 下钻单元：同一列、上下紧挨的 2 张桌子（悬停即提示是哪两桌，点击高亮后进入这两桌详情） -->
            <div
              v-for="p in pr"
              :key="p.index"
              class="pair"
              :class="{ hot: hotPair === p.index, pending: pendingPair === p.index }"
              :title="`查看 ${p.code} 两桌（${p.stationCount} 个工位）详情`"
              @mouseenter="hotPair = p.index"
              @mouseleave="hotPair = null"
              @click="openPair(p.index)"
            >
              <!-- 桌子内不再重复标编号：行号统一标在每一横排最左侧，列信息不再标注 -->
              <div v-for="t in p.tables" :key="t.code" class="table">
                <div class="t-stations">
                  <span
                    v-for="st in t.stations"
                    :key="st.id"
                    class="ws"
                    :class="['st-' + st.status, { matched: statusFiltering && isMatch(st), dim: !inModelScope(st) }]"
                    @mouseenter="showWsTip($event, st, t)"
                    @mouseleave="hideWsTip"
                  >
                    <!-- 只显示在制产品名：最多两行；装不下 → 开页滚动一次就停，
                         鼠标移到这一组（上下两排）时，只有这一组的工位循环滚动 -->
                    <span
                      class="w-name"
                      :class="{ 'is-play': introScrolling && hotPair !== p.index, 'is-loop': hotPair === p.index }"
                      v-marquee="subTxt(st)"
                    >
                      <span class="w-name-in">
                        <span class="w-seg">{{ subTxt(st) }}</span>
                        <span class="w-seg">{{ subTxt(st) }}</span>
                      </span>
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <!-- ===== 底部实时告警滚动条 ===== -->
      <footer class="ticker panel">
        <span class="ticker-label"><i></i>实时告警</span>
        <div class="ticker-viewport">
          <div ref="tickerTrack" class="ticker-track">
            <span v-for="(a, i) in tickerItems" :key="i" class="tk" :class="a.sev"><b>{{ a.time }}</b>{{ a.text }}</span>
          </div>
        </div>
      </footer>

    </div>

    <!-- ===== 工位悬浮信息卡（跟随鼠标所在工位显示基本信息） ===== -->
    <div
      v-if="hoverWs"
      class="ws-tip"
      :class="'tip-' + hoverWs.st.status"
      :style="tipStyle"
    >
      <div class="tip-head">
        <span class="tip-dot"></span>
        <b>{{ hoverWs.st.id }}</b>
        <em>{{ tipState }}</em>
      </div>
      <!-- ✅ 悬浮内容按需求改为：在制型号 / 产品进程 / 产品执行状态 / 合同号 / 最终业主 -->
      <div class="tip-rows">
        <div class="tip-row"><span>在制型号</span><b>{{ hoverWs.st.product ? hoverWs.st.product.model : '—' }}</b></div>
        <div class="tip-row"><span>产品进程</span><b>{{ processText(hoverWs.st.product && hoverWs.st.product.processStatus) }}</b></div>
        <div class="tip-row"><span>产品执行状态</span><b>{{ hoverWs.st.product ? executeText(hoverWs.st.product.executeStatus) : '未绑定产品' }}</b></div>
        <div class="tip-row"><span>合同号</span><b>{{ hoverWs.st.product ? (hoverWs.st.product.contractNo || hoverWs.st.product.contract || '—') : '—' }}</b></div>
        <div class="tip-row"><span>最终业主</span><b>{{ hoverWs.st.product ? (hoverWs.st.product.owner || '—') : '—' }}</b></div>
      </div>
      <div v-if="hoverWs.st.note" class="tip-note">{{ hoverWs.st.note }}</div>
    </div>
  </div>
</template>

<script>
import UserInfoUtils from '@/utils/userInfoUtils.js';
/* 工位数据（真实接口 getStationList）+ 文案字典：与工位详情看板（StationInfoGas）
   共用同一套数据层 ./js/stationGasData.js，避免两个页面各写一份导致口径不一致 */
import {
  fetchBoard, openStationStream, seriesOf, STATUS_TXT,
  processText, executeText, pad2, nowTime, ZONE_CODE,
} from './js/stationGasData.js';
/* 全屏开关：优先原生 Fullscreen API，不支持时自动退回铺满视口（与工位详情页共用同一份实现） */
import { toggleFullscreen, onFullscreenChange } from './js/fullscreen.js';

/* ============================================================
   工位总览（气体）· A区（流程区）
   - 数据：SSE 实时推送 getStationListSse（后端的 GET api/dash/cockpit/station/sse，
     鉴权 token 走 Query params），打开页面就订阅，后端每 5 秒推一次本区最新工位信息；
     首帧为尽快出画仍用 getStationList 拉一次全量；后端一行 = 一个工位
     （位置信息 + 绑定的在制产品信息），收到就整盘替换
   - 布局 / 编号：统一在 ./js/stationGasData.js（与工位详情看板共用）
     按 row_no 分行、col_no 分桌、pos_no 分位
     桌子 A.{行:02}.{列:02}，工位 A.{行:02}.{列:02}.{位:02}（即后端 station_id）
   - 下钻单元：同一列、上下紧挨的 2 张桌子（图上不标序号，点击这两桌即高亮并进入它们详情）
     → 工位详情看板 /stationInfoGas?pair=N（展示这两张桌子的工位）
   - 每个工位绑定一件在制产品：卡片显示该产品的【产品型号】
   - 工位颜色 = 是否绑有产品 + 产品执行状态：
     未绑产品 idle 灰 / 6 正常 normal 绿 / 7 暂停 warning 黄 / 8 停工 error 红
   - 悬浮工位卡片：所在桌子 · 在制型号 · 产品进程 · 产品执行状态
   - 底部告警流：初始取当前停工 / 暂停工位，之后每次刷新比对状态变化补一条
   ============================================================ */

const TICKER_SPEED = 55; // 告警滚动速度 px/s

/* ---------- 工位卡内产品名：最多两行，两行还装不下就从下往上单向滚动显示全部 ----------
   用法：<span class="w-name" v-marquee="文本"><span class="w-name-in">…两份文本…</span></span>
   两份相同文本上下相接，动画 0 → -50% 正好接上一轮（首尾无缝），方向固定从下往上
   指令只负责「要不要滚 / 滚多快」（can-scroll + --dur），
   什么时候滚由模板的 is-play（开页一次）/ is-loop（悬停该组）决定
   文本或卡片尺寸变了才重新测量，避免每次 tick 重排动画 */
const MARQUEE_SPEED = 10;  // 纵向滚动速度 px/s
const MARQUEE_MIN = 2.5;   // 一轮最短时长 s
const MARQUEE_MAX = 6;     // 一轮最长时长 s
const INTRO_MS = 6600;     // 开页首播窗口：略大于最长一轮（MARQUEE_MAX）
function marqueeFit(el, text) {
  const inner = el.firstElementChild;
  if (!inner) return;
  el.__mText = text;
  el.__mW = el.clientWidth;
  el.__mH = el.clientHeight;
  el.classList.remove('can-scroll');
  el.__mScroll = false;
  inner.style.removeProperty('animation-duration');
  requestAnimationFrame(() => {
    const textH = inner.scrollHeight;        // 文本实际高度（此时只显示第一份）
    el.__mScroll = textH - el.clientHeight > 1; // 超过两行才需要滚
    if (!el.__mScroll) return;
    el.classList.add('can-scroll');
    el.__mDur = `${Math.min(MARQUEE_MAX, Math.max(MARQUEE_MIN, textH / MARQUEE_SPEED)).toFixed(1)}s`;
    inner.style.setProperty('animation-duration', el.__mDur);
    el.__mW = el.clientWidth;
    el.__mH = el.clientHeight;
  });
}
const marquee = {
  inserted(el, binding) {
    marqueeFit(el, binding.value);
    if (typeof ResizeObserver !== 'undefined') {
      el.__mRo = new ResizeObserver(() => marqueeFit(el, el.__mText));
      el.__mRo.observe(el);
    }
  },
  update(el, binding) {
    if (el.__mText === binding.value && el.__mW === el.clientWidth && el.__mH === el.clientHeight) {
      // Vue 重写 class 时可能把 can-scroll 冲掉，这里补回来（播放类仍由模板控制）
      if (el.__mScroll && !el.classList.contains('can-scroll')) {
        el.classList.add('can-scroll');
        const inner = el.firstElementChild;
        if (inner && el.__mDur) inner.style.setProperty('animation-duration', el.__mDur);
      }
      return;
    }
    marqueeFit(el, binding.value);
  },
  unbind(el) {
    if (el.__mRo) { el.__mRo.disconnect(); el.__mRo = null; }
  },
};

export default {
  name: 'StationViewGas',
  directives: { marquee },
  data() {
    return {
      now: '--:--:--',
      dateText: '----年--月--日',
      shift: '白班',
      zone: ZONE_CODE, // 当前看板对应的区（processCode）
      rows: [],      // 行结构（总览按行铺桌子；行数由接口数据决定）
      pairs: [],     // 下钻单元（同一列上下相邻 2 桌）
      pairRows: [],  // 单元按行分组（排版用）
      loading: false, // 接口请求中
      loadErr: '',    // 接口失败提示（成功时置空）
      sse: null,      // SSE 连接（EventSource），离开页面要 .close()
      sseErr: '',     // SSE 断流提示（EventSource 会自动重连，恢复后置空）
      lastBoardAt: 0, // 当前显示这批数据的时刻：更旧的数据不再回写覆盖
      prevStatus: null, // 上一次各工位状态（用来比对本次刷新有哪些工位变了）
      hotPair: null, // 悬停中的单元下标：先让用户看清是哪两桌
      pendingPair: null, // 已点击待跳转的单元下标：两桌一起点亮
      hoverWs: null, // 悬浮中的工位 { st, table, rect }
      introScrolling: true, // 开页首播：产品名称只滚一次，播完置 false
      filterStatuses: [], // 多选筛选：选中的状态（['normal','warning']…），空数组 = 不限
      filterModels: [],   // 多选筛选：选中的在制型号系列（['PA500','LA300']…），空数组 = 不限
      filterContract: '', // ✅ 关键字筛选：合同号（模糊匹配，空 = 不限）
      filterOwner: '',    // ✅ 关键字筛选：最终业主（模糊匹配，空 = 不限）
      alerts: [],
      tickerItems: [],
      fs: false, // 是否处于全屏（按钮图标 / 提示跟着切）
    };
  },
  computed: {
    statusTxt() { return STATUS_TXT; },
    // A 区整体统计
    stats() {
      const c = { total: 0, ok: 0, warn: 0, err: 0, idle: 0 };
      this.walkStations((s) => {
        c.total += 1;
        if (s.status === 'normal') c.ok += 1;
        else if (s.status === 'warning') c.warn += 1;
        else if (s.status === 'error') c.err += 1;
        else c.idle += 1;
      });
      return c;
    },
    // 本班完工：接口没有单独的完工流水，这里按「本区在制产品里已完工（产品进程 = 9）」的数量显示
    doneTotal() {
      let n = 0;
      this.walkStations((s) => {
        if (s.product && s.product.processStatus === 9) n += 1;
      });
      return n;
    },
    // 悬浮卡标题右侧：显示该工位在制产品的执行状态（未绑产品 = 空闲）
    tipState() {
      const st = this.hoverWs && this.hoverWs.st;
      if (!st) return '';
      if (!st.product) return '空闲（未绑定产品）';
      return executeText(st.product.executeStatus);
    },
    // 悬浮卡定位：优先显示在工位上方，贴边时自动翻转
    tipStyle() {
      const t = this.hoverWs;
      if (!t || !t.rect) return {};
      const W = 232;
      const H = 152;   // ✅ 悬浮卡现在是 5 行（加了合同号 / 最终业主），高度跟着加，贴边翻转才准
      const r = t.rect;
      let x = r.left + r.width / 2 - W / 2;
      x = Math.min(Math.max(10, x), window.innerWidth - W - 10);
      let y = r.top - H - 10;
      if (y < 10) y = Math.min(r.bottom + 10, window.innerHeight - H - 10);
      return { left: `${Math.round(x)}px`, top: `${Math.round(y)}px` };
    },
    /* 各在制型号系列的工位数（看板头部产品按钮用，全量实时统计）
       系列清单不再写死：完全按当前页面实际出现的在制型号统计，有哪个系列就显示哪个
       （这次有 LA301 / LA200 就显示 LA301 / LA200，没有 PA200 就不显示 PA200），
       型号按系列截断（PA600-RQDEx → PA600、LA301-xxx → LA301），不细分到具体型号；
       SSE 每 5 秒推一次数据，系列清单和数量都跟着实时变 */
    modelStats() {
      const map = new Map();
      this.walkStations((st) => {
        if (!this.inKeywordScope(st)) return;   // ✅ 先过关键字，系列清单 / 数量跟着关键字变
        const s = seriesOf(st.model);
        if (!s) return;   // 未绑产品（model 为空）不计入任何系列
        map.set(s, (map.get(s) || 0) + 1);
      });
      return [...map.keys()].sort().map((s) => ({ series: s, count: map.get(s) }));
    },
    // 状态筛选项（带实时数量）：看板头部状态按钮用
    statusOptions() {
      // 数量按"当前选中的产品型号系列"重新汇总：未选产品 = 全量统计；
      // 选了 PA500 后，这里就是 PA500 这一批工位的状态汇总（正常 / 预警 / 异常 / 空闲各多少）
      const c = { normal: 0, warning: 0, error: 0, idle: 0 };
      this.walkStations((st) => {
        if (!this.inModelScope(st)) return;
        if (c[st.status] !== undefined) c[st.status] += 1;
      });
      return [
        { key: 'normal', label: '正常', cls: 'c-ok', count: c.normal },
        { key: 'warning', label: '暂停', cls: 'c-wn', count: c.warning },
        { key: 'error', label: '停工', cls: 'c-er', count: c.error },
        { key: 'idle', label: '空闲', cls: 'c-id', count: c.idle },
      ];
    },
    // 是否处于筛选态：状态多选 / 型号系列多选 / 关键字，任意一组有选中即生效
    filterActive() {
      return this.filterStatuses.length > 0
        || this.filterModels.length > 0
        || !!(this.filterContract || '').trim()
        || !!(this.filterOwner || '').trim();
    },
    // 工位视觉口径（三档，见模板上的类绑定）：
    //   高亮 = 产品命中 且 状态命中     → matched: statusFiltering && isMatch(st)
    //   置灰 = 产品没命中（未选产品不触发）→ dim: !inModelScope(st)
    //   原样 = 产品命中、状态没命中     → 彩色但不高亮
    // 是否"按状态细分"：只有选了状态时才给命中工位加高亮（只选产品时命中工位保持初始样）
    statusFiltering() {
      return this.filterStatuses.length > 0;
    },
    // 已选条件文案：产品是范围、状态是范围内的细分（如"产品 PA500/LA300 · 状态 预警"）
    filterText() {
      const parts = [];
      if (this.filterModels.length) parts.push(`产品 ${this.filterModels.join('/')}`);
      if ((this.filterContract || '').trim()) parts.push(`合同号 ${this.filterContract.trim()}`);
      if ((this.filterOwner || '').trim()) parts.push(`业主 ${this.filterOwner.trim()}`);
      if (this.filterStatuses.length) parts.push(`状态 ${this.filterStatuses.map((k) => STATUS_TXT[k] || k).join('/')}`);
      return parts.join(' · ');
    },
    // 当前筛选命中的工位数量（产品范围 ∩ 状态）
    filterCount() {
      if (!this.filterActive) return 0;
      let n = 0;
      this.walkStations((st) => { if (this.isMatch(st)) n += 1; });
      return n;
    },
  },
  watch: {
    /* 系列清单随数据实时变：已选但当前页面已经没有的系列要自动移出筛选，
       否则会出现「条件还亮着，但一个工位都命中不了」的空筛选 */
    modelStats(stats) {
      const alive = stats.map((m) => m.series);
      const kept = this.filterModels.filter((s) => alive.indexOf(s) !== -1);
      if (kept.length !== this.filterModels.length) this.filterModels = kept;
    },
  },
  methods: {
    /* ---------- 导航 ---------- */
    backHome() {
      // 返回主页：按权限决定默认主页，避免跳转到无权限的项目页
      this.$router.push(UserInfoUtils.getDefaultHomeRoute());
    },

    /* ---------- 全屏 ---------- */
    // 整个看板铺满屏幕：优先原生全屏，不支持时自动退回「铺满视口」
    toggleFs() {
      toggleFullscreen(this.$el, (on) => { this.fs = on; });
    },

    /* ---------- 下钻：同一列相邻的 2 张桌子为一个单元 ---------- */
    openPair(index) {
      if (this.pendingPair !== null) return; // 防连点
      // 先把这两张桌子一起点亮，再进入它们的详情页（让用户看清点中的是哪两桌）
      this.pendingPair = index;
      this.hotPair = index;
      setTimeout(() => {
        this.$router.push({ path: '/stationInfoGas', query: { pair: String(index) } });
      }, 160);
    },

    /* ---------- 多选筛选：产品型号系列（范围）× 状态（范围内细分） ---------- */
    // 状态按钮是否点亮
    isStatusOn(key) { return this.filterStatuses.indexOf(key) !== -1; },
    // 型号系列按钮是否点亮
    isModelOn(series) { return this.filterModels.indexOf(series) !== -1; },
    /* ✅ 关键字筛选：合同号 / 最终业主
       模糊匹配（包含即可）、不区分大小写、忽略首尾空格；
       两个关键字之间是「并且」，并且与型号、状态筛选也是「并且」 */
    inKeywordScope(st) {
      const c = (this.filterContract || '').trim().toLowerCase();
      const o = (this.filterOwner || '').trim().toLowerCase();
      if (!c && !o) return true;                 // 都没填 = 不限
      const p = st.product;
      if (!p) return false;                      // 没绑产品 → 不可能命中关键字
      // 合同号优先取 contract_no，没有再退回 contract（与详情页、悬浮卡同一口径）
      const cv = String(p.contractNo || p.contract || '').toLowerCase();
      const ov = String(p.owner || '').toLowerCase();
      if (c && cv.indexOf(c) === -1) return false;
      if (o && ov.indexOf(o) === -1) return false;
      return true;
    },
    // 是否落在当前"产品范围"内：关键字 + 型号系列，两者都满足才算在范围内
    inModelScope(st) {
      if (!this.inKeywordScope(st)) return false;
      if (!this.filterModels.length) return true;
      // 型号按系列截断比对：选 PA500 时 PA500-1000-GI00 / PA500-1000-DI00 … 都在范围内
      return this.filterModels.indexOf(seriesOf(st.model)) !== -1;
    },
    // 命中判定：先过产品范围，再按状态细分（两组取交集）
    isMatch(st) {
      if (!this.filterActive) return false;
      if (!this.inModelScope(st)) return false;
      // 只选产品没选状态 → 该产品的工位全部命中
      if (!this.filterStatuses.length) return true;
      return this.filterStatuses.indexOf(st.status) !== -1;
    },
    // 点型号系列按钮：加入 / 移出筛选集合（可多选，点几个筛几个）
    toggleModel(series) {
      const i = this.filterModels.indexOf(series);
      if (i !== -1) { this.filterModels.splice(i, 1); return; }
      // 当前没有在制工位的系列不进集合：避免"点亮了却一个都没命中"的空筛选
      if (this.modelStats.some((m) => m.series === series && m.count > 0)) this.filterModels.push(series);
    },
    // 点状态按钮：加入 / 移出筛选集合（可多选，点哪些高亮哪些）
    toggleStatus(key) {
      const i = this.filterStatuses.indexOf(key);
      if (i !== -1) { this.filterStatuses.splice(i, 1); return; }
      // 数量为 0 的状态不进集合，同上
      if (this.statusOptions.some((s) => s.key === key && s.count > 0)) this.filterStatuses.push(key);
    },
    clearFilter() {
      this.filterStatuses = [];
      this.filterModels = [];
      this.filterContract = '';   // ✅ 关键字一起清掉
      this.filterOwner = '';
    },

    /* ---------- 工位悬浮：高亮 + 基本信息卡 ---------- */
    showWsTip(e, st, table) {
      const r = e.currentTarget.getBoundingClientRect();
      this.hoverWs = { st, table, rect: { left: r.left, top: r.top, right: r.right, bottom: r.bottom, width: r.width, height: r.height } };
    },
    hideWsTip() {
      this.hoverWs = null;
    },

    /* ---------- 统计 ---------- */
    walkStations(fn) {
      this.rows.forEach((row) => row.tables.forEach((t) => t.stations.forEach(fn)));
    },
    /* ---------- 卡片显示：只显示在制产品的【产品型号】（未绑产品显示空闲） ---------- */
    subTxt(st) {
      if (!st.product) return '空闲';
      return st.product.model;
    },
    /* 产品进程 / 产品执行状态文案（悬浮卡用） */
    processText(v) { return processText(v); },
    executeText(v) { return executeText(v); },

    /* ---------- 告警滚动条 ---------- */
    pushAlert(sev, text) {
      this.alerts.unshift({ sev, time: nowTime(), text });
      if (this.alerts.length > 10) this.alerts.pop();
      this.renderTicker();
    },
    renderTicker() {
      const half = [];
      for (let i = 0; i < 3; i++) half.push(...this.alerts);
      this.tickerItems = [...half, ...half]; // 双份内容实现无缝循环
      this.$nextTick(() => {
        const track = this.$refs.tickerTrack;
        if (!track) return;
        const w = track.scrollWidth / 2;
        track.style.animationDuration = Math.max(30, Math.round(w / TICKER_SPEED)) + 's';
      });
    },
    // 告警严重度：跟着工位状态走（接口只给状态，不给原因）
    sevOf(status) {
      if (status === 'error') return 'error';
      if (status === 'warning') return 'warning';
      return 'info';
    },
    // 初始告警流：直接取当前数据里的停工 / 暂停工位（不再编造原因文案）
    seedAlerts() {
      const errs = [];
      const warns = [];
      let idle = 0;
      this.walkStations((st) => {
        if (st.status === 'error') errs.push(st);
        else if (st.status === 'warning') warns.push(st);
        else if (st.status === 'idle') idle += 1;
      });
      const list = [];
      errs.slice(0, 3).forEach((st) =>
        list.push({ sev: 'error', time: nowTime(), text: `工位 ${st.id} ${st.model || st.stationStatus || ''} 执行状态：停工` }));
      warns.slice(0, 2).forEach((st) =>
        list.push({ sev: 'warning', time: nowTime(), text: `工位 ${st.id} ${st.model || st.stationStatus || ''} 执行状态：暂停` }));
      list.push({ sev: 'done', time: nowTime(), text: `${this.zone} 区在制工位 ${this.stats.ok + this.stats.warn + this.stats.err} 个` });
      list.push({ sev: 'info', time: nowTime(), text: `${this.zone} 区空闲工位 ${idle} 个，等待上件` });
      this.alerts = list;
      this.renderTicker();
    },
    // 本次刷新有哪些工位状态变了 → 补进告警流（只取最近几条，避免刷屏）
    diffAlerts() {
      const cur = {};
      this.walkStations((st) => { cur[st.id] = st.status; });
      const prev = this.prevStatus;
      this.prevStatus = cur;
      if (!prev) return [];
      const changes = [];
      Object.keys(cur).forEach((id) => {
        const before = prev[id];
        if (before && before !== cur[id]) {
          changes.push({ id, sev: this.sevOf(cur[id]), text: `执行状态：${STATUS_TXT[before] || before} → ${STATUS_TXT[cur[id]] || cur[id]}` });
        }
      });
      return changes;
    },

    /* ---------- 数据刷新：SSE 实时推送（首帧用 REST 兜底） ---------- */
    // 工位数据落到界面：SSE 推来的和首帧 REST 拉的都走这里
    // at 是这条数据的「出生时刻」，比当前显示的还旧就丢掉，防止慢请求把新数据写回去
    applyBoard(board, isRefresh, at) {
      if (at && at < this.lastBoardAt) return false;
      if (at) this.lastBoardAt = at;
      this.rows = board.rows;
      this.pairs = board.pairs;
      this.pairRows = board.pairRows;
      this.loadErr = '';
      this.sseErr = '';
      if (!isRefresh) {
        this.seedAlerts();
        this.prevStatus = null;
        this.diffAlerts(); // 首次只记录基线，不刷告警
      } else {
        const changes = this.diffAlerts();
        changes.slice(0, 4).forEach((c) => this.pushAlert(c.sev, `工位 ${c.id} ${c.text}`));
      }
      return true;
    },
    // 首帧：进页面先用 REST 拉一次全量，保证立刻有画面
    async loadBoard(isRefresh) {
      const at = Date.now();
      if (this.loading) return;
      this.loading = true;
      try {
        const board = await fetchBoard(this.zone); // processCode = 当前区
        this.applyBoard(board, isRefresh, at);
      } catch (e) {
        this.loadErr = '工位数据加载失败：' + (e.message || '网络错误');
      } finally {
        this.loading = false;
      }
    },
    /* 打开页面就订阅 SSE：后端每 5 秒推本区最新工位信息，收到就整盘替换 */
    openStream() {
      this.closeStream();
      this.sse = openStationStream(this.zone, {
        onBoard: (board) => {
          this.sseErr = '';
          this.applyBoard(board, !!this.lastBoardAt, Date.now());
        },
        // EventSource 自带重连，这里只负责把状态显示出来
        onError: () => { this.sseErr = '实时推送连接中断，正在自动重连…'; },
      });
    },
    closeStream() {
      if (this.sse) { this.sse.close(); this.sse = null; }
    },

    /* ---------- 时钟 ---------- */
    tickClock() {
      const d = new Date();
      this.now = nowTime();
      const week = '日一二三四五六'[d.getDay()];
      this.dateText = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} 星期${week}`;
      const h = d.getHours();
      this.shift = (h >= 8 && h < 20) ? '白班' : '夜班';
    },

    /* ---------- 初始化 ---------- */
    init() {
      this.tickClock();
      this.loadBoard(false); // 首帧：REST 拉一次全量（含绑定的在制产品）
      this.openStream();     // 之后全由 SSE 推：后端每 5 秒一条，不再轮询
      this.timerClock = setInterval(this.tickClock, 1000);
      // 开页首播：等 DOM 渲染后计时，播完一轮就停（之后只由鼠标悬停触发）
      this.$nextTick(() => {
        this.introTimer = setTimeout(() => { this.introScrolling = false; }, INTRO_MS);
      });
    },
  },
  mounted() {
    this.init();
    // 原生全屏按 ESC / F11 退出时不经过按钮，靠这个把按钮图标同步回来
    this.offFsChange = onFullscreenChange((on) => { this.fs = on; });
  },
  beforeDestroy() {
    this.closeStream();   // 离开关掉 SSE 长连接
    clearInterval(this.timerClock);
    clearTimeout(this.introTimer);
    if (this.offFsChange) this.offFsChange(); // 摘掉全屏状态监听
  },
};
</script>

<style scoped>
/* ================= 暗色大屏主题 =================
   注意：组件使用 scoped 样式，CSS 变量必须定义在组件根元素 .gas-cockpit 上
   （若定义在 :root 上，scoped 编译后不生效，导致 var(--xx) 全部失效） */
.gas-cockpit {
  /* 与 StationInfoGas 完全同款：深蓝底 + 亮青主色；卡片纯色实底、文字高对比 */
  --bg: #072143;                    /* 深蓝底 */
  --panel: #072244;                 /* 内容面板：纯色实底 */
  --card: #0a2b52;                  /* 卡片底色：纯色实底，卡片里所有混色都以它为底 */
  --line: rgba(0, 174, 255, 0.38);
  --line-soft: rgba(0, 174, 255, 0.18);
  --txt: #ffffff;                   /* 主文字：纯白，强对比 */
  --txt-dim: #8eb5c9;               /* 次要文字：亮钢蓝 */
  --ok: #1dd183;    /* 正常 · 绿 */
  --warn: #feca57;  /* 预警 · 黄 */
  --err: #ff6b6b;   /* 异常 · 红 */
  --idle: #9aa7b8;  /* 未绑产品 · 灰 */
  --accent: #00aeff;
  --accent-2: #00c3ff;
  /* 全站统一字体：微软雅黑（数字、编号、标题都走这一套） */
  --font-num: "Microsoft YaHei", "PingFang SC", "Noto Sans SC", sans-serif;

  position: relative;
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  /* 纯色深蓝底：不要网格、不要光晕、不要渐变 */
  background: var(--bg);
  color: var(--txt);
  font-family: "Microsoft YaHei", "PingFang SC", "Noto Sans SC", sans-serif;
  -webkit-font-smoothing: antialiased;
  user-select: none;
}
.gas-cockpit, .gas-cockpit * { margin: 0; padding: 0; box-sizing: border-box; }
.gas-cockpit ::selection { background: rgba(0, 174, 255, .35); }

/* ---------- 舞台 ---------- */
.gas-stage {
  flex: 1; min-width: 0; min-height: 0;
  width: 100%; height: 100%;
  display: flex; flex-direction: column;
  padding: 16px 30px 14px; gap: 12px;
}
.panel {
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 10px;
}

/* ---------- 顶部信息栏 ---------- */
.topbar {
  flex: 0 0 72px;
  display: flex; align-items: center;
  padding: 0 18px; gap: 14px;
  position: relative;
}
.topbar::after {
  content: ""; position: absolute; left: 0; right: 0; bottom: 0; height: 2px;
  border-radius: 0 0 10px 10px;
  background: rgba(0, 174, 255, .5);
}

.brand { display: flex; align-items: center; gap: 12px; min-width: 0; }
.logo {
  width: 40px; height: 40px; border-radius: 10px; flex: 0 0 auto;
  color: var(--accent-2);
  border: 1px solid rgba(0, 174, 255, .55);
  background: rgba(5, 149, 235, .18);
  display: grid; place-items: center;
}
.logo svg { width: 26px; height: 26px; }
/* 标题：亮青实色字 */
.brand-txt h1 {
  font-size: 22px; font-weight: 700; letter-spacing: 3px;
  color: var(--accent-2);
  white-space: nowrap;
}
.brand-txt p { margin-top: 2px; font-size: 10px; letter-spacing: 2.5px; color: var(--txt-dim); white-space: nowrap; }
.mode-tag {
  margin-left: 6px; padding: 4px 12px;
  font-size: 11.5px; color: var(--accent-2);
  border: 1px solid rgba(0, 174, 255, .5);
  background: rgba(5, 149, 235, .2);
  border-radius: 999px; white-space: nowrap;
}

/* ✅ 标题栏关键字筛选：合同号 / 最终业主（与下方型号、状态筛选叠加） */
.brand-filter { display: flex; align-items: center; gap: 8px; margin-left: 10px; }
.kw {
  display: flex; align-items: center; gap: 5px;
  height: 28px; padding: 0 8px;
  border: 1px solid rgba(0, 174, 255, .38);
  border-radius: 6px;
  background: rgba(5, 149, 235, .12);
  transition: border-color .15s, background .15s;
}
.kw:hover { border-color: rgba(0, 174, 255, .7); background: rgba(5, 149, 235, .2); }
/* 填了关键字：与下方「选中的 chip」一样点亮 */
.kw.on {
  border-color: rgba(0, 174, 255, .95);
  background: rgba(5, 149, 235, .3);
  box-shadow: 0 0 0 1px rgba(0, 174, 255, .35) inset;
}
.kw-label { font-size: 11px; color: var(--accent-2); white-space: nowrap; letter-spacing: .5px; }
.kw-input {
  width: 96px; height: 20px;
  border: 0; outline: none; padding: 0;
  background: transparent;
  color: #ffffff; font-family: inherit; font-size: 12px;
  min-width: 0;
}
.kw-input::placeholder { color: rgba(255, 255, 255, .38); }
.kw-clear {
  flex: 0 0 auto;
  width: 16px; height: 16px; line-height: 14px;
  padding: 0; border: 0; border-radius: 50%;
  cursor: pointer; font-family: inherit; font-size: 13px;
  color: var(--accent-2);
  background: rgba(255, 255, 255, .12);
}
.kw-clear:hover { color: #ffffff; background: rgba(255, 107, 107, .6); }
/* 屏幕不够宽时收窄输入框，别把顶栏挤出去 */
@media (max-width: 1400px) {
  .brand-filter { gap: 6px; margin-left: 6px; }
  .kw { height: 26px; padding: 0 6px; }
  .kw-input { width: 70px; font-size: 11.5px; }
  .kw-label { font-size: 10.5px; }
}

/* 核心指标 */
.metrics { display: flex; margin-left: auto; }
.metric { padding: 0 15px; border-left: 1px solid rgba(0, 174, 255, .28); display: flex; flex-direction: column; gap: 3px; }
.m-label { font-size: 10.5px; color: var(--txt-dim); letter-spacing: 1px; white-space: nowrap; }
.m-value b { font-family: var(--font-num); font-size: 24px; font-weight: 700; color: #ffffff; }
.m-value small { font-size: 11px; color: var(--txt-dim); margin-left: 3px; font-weight: 400; }
.m-value.c-cyan b { color: var(--accent); }
.m-value.c-green b { color: var(--ok); }
.m-value.c-red b { color: var(--err); }

/* 时钟 */
.clock-box { padding-left: 15px; border-left: 1px solid rgba(0, 174, 255, .28); text-align: right; }
.clock-time { font-family: var(--font-num); font-size: 24px; font-weight: 700; letter-spacing: 1px; color: #ffffff; white-space: nowrap; }
.clock-sub { margin-top: 2px; font-size: 10.5px; color: var(--txt-dim); display: flex; align-items: center; justify-content: flex-end; gap: 8px; white-space: nowrap; }
.shift { padding: 1px 9px; border-radius: 999px; font-size: 10px; color: var(--accent-2); border: 1px solid rgba(0, 174, 255, .5); background: rgba(5, 149, 235, .2); }

/* 返回主页：仅一个小房子图标 */
.back-btn {
  flex: 0 0 auto;
  width: 30px; height: 30px;
  display: grid; place-items: center;
  padding: 0; border: 0; border-radius: 8px;
  color: var(--accent); background: transparent;
  cursor: pointer; font-family: inherit;
  transition: color .15s, background .15s, transform .15s;
}
.back-btn svg { width: 18px; height: 18px; }
.back-btn:hover { color: #ffffff; background: rgba(0, 174, 255, .32); transform: translateY(-1px); }

/* 全屏按钮：与「返回主页」同尺寸同观感，放在它左边 */
.fs-btn {
  flex: 0 0 auto;
  width: 30px; height: 30px;
  display: grid; place-items: center;
  padding: 0; border: 0; border-radius: 8px;
  color: var(--accent); background: transparent;
  cursor: pointer; font-family: inherit;
  transition: color .15s, background .15s, transform .15s;
}
.fs-btn svg { width: 18px; height: 18px; }
.fs-btn:hover { color: #ffffff; background: rgba(0, 174, 255, .32); transform: translateY(-1px); }
.fs-btn:active { transform: translateY(0); }
/* 原生全屏 / 兜底伪全屏时都要铺满整个屏幕 */
.gas-cockpit:fullscreen { width: 100vw; height: 100vh; background: var(--bg); }
.gas-cockpit.is-fs {
  position: fixed !important; inset: 0; z-index: 1200;
  width: 100vw; height: 100vh; background: var(--bg);
}
.back-btn:active { transform: translateY(0); }

/* ---------- A 区 · 流程区 看板（14 行 × 4 列桌子，每 2 行一组） ---------- */
.board {
  flex: 1; min-height: 0;
  display: flex; flex-direction: column;
  padding: 9px 14px 12px;
}
.board-head { flex: 0 0 30px; display: flex; align-items: center; justify-content: space-between; gap: 12px; min-width: 0; }
.b-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
.z-mark { width: 4px; height: 16px; border-radius: 2px; flex: 0 0 auto; background: var(--accent-2); }
.board-head h2 { font-size: 15px; font-weight: 700; letter-spacing: 2px; color: #ffffff; white-space: nowrap; }
.z-code {
  font-family: var(--font-num); font-size: 9.5px; letter-spacing: 1.5px; color: var(--txt-dim);
  padding: 1px 8px; border: 1px solid var(--line); border-radius: 4px; white-space: nowrap;
}
.z-meta { font-size: 11px; color: var(--txt-dim); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.b-right { display: flex; align-items: center; gap: 6px; flex: 0 1 auto; min-width: 0; }
/* 筛选胶囊：状态色混深蓝实底，纯色不加辉光 */
.chip {
  font-size: 11px; padding: 2px 10px; border-radius: 999px;
  border: 1px solid; white-space: nowrap; display: flex; align-items: center; gap: 5px;
  flex: 0 0 auto; /* 不参与压缩，避免窄屏时被挤扁/切掉圆角 */
}
.chip b { font-family: var(--font-num); font-size: 12px; font-weight: 700; }
.chip.c-ok { color: var(--ok); border-color: rgba(29, 209, 161, .55); background: color-mix(in srgb, var(--ok) 18%, var(--panel)); }
.chip.c-wn { color: #ffd882; border-color: rgba(254, 202, 87, .55); background: color-mix(in srgb, var(--warn) 18%, var(--panel)); }
.chip.c-er { color: #ff8f8f; border-color: rgba(255, 107, 107, .6); background: color-mix(in srgb, var(--err) 20%, var(--panel)); }
.chip.c-id { color: #b7d3e6; border-color: rgba(142, 181, 201, .5); background: color-mix(in srgb, #8eb5c9 16%, var(--panel)); }
/* 全部（清除筛选）：未筛选时点亮 */
.chip.c-all { color: var(--accent-2); border-color: rgba(0, 174, 255, .55); background: color-mix(in srgb, #00aeff 22%, var(--panel)); }
.i-all { width: 11px; height: 11px; fill: currentColor; flex: 0 0 auto; }

/* 可点击筛选 chip：常态可点，点中即点亮 */
.chip.btn {
  font-family: inherit; cursor: pointer;
  transition: transform .15s, background .15s, border-color .15s;
}
.chip.btn:hover { transform: translateY(-1px); }
/* 当前产品范围内数量为 0 的状态：淡显 + 不可点（避免点亮后一个都不命中的空筛选） */
.chip.btn.zero { opacity: .45; cursor: not-allowed; }
.chip.btn.zero:hover { transform: none; }
/* 已选中但当前范围内为 0：保持可见，方便用户点掉 */
.chip.btn.on.zero { opacity: .75; }
.chip.btn.on { font-weight: 700; }
.chip.btn.on b { color: #ffffff; }
/* 选中：状态色占比拉高的实底 + 一圈同色描边（不加辉光、不做呼吸动画） */
.chip.c-ok.on { color: #ffffff; border-color: var(--ok); background: color-mix(in srgb, var(--ok) 55%, var(--panel)); }
.chip.c-wn.on { color: #ffffff; border-color: var(--warn); background: color-mix(in srgb, var(--warn) 55%, var(--panel)); }
.chip.c-er.on { color: #ffffff; border-color: var(--err); background: color-mix(in srgb, var(--err) 55%, var(--panel)); }
.chip.c-id.on { color: #ffffff; border-color: var(--idle); background: color-mix(in srgb, #8eb5c9 45%, var(--panel)); }
.chip.c-all.on { color: #ffffff; border-color: var(--accent-2); background: color-mix(in srgb, #00aeff 45%, var(--panel)); }

/* 各型号系列在制工位统计（按系列聚合） */
.m-chips { display: flex; align-items: center; gap: 6px; min-width: 0; flex: 0 0 auto; }
.chip.c-md {
  font-family: var(--font-num); font-size: 10.5px; padding: 1px 8px; gap: 4px;
  color: #cdddf7; border-color: rgba(0, 174, 255, .35); background: color-mix(in srgb, #00aeff 12%, var(--panel));
}
.chip.c-md b { font-size: 11px; color: var(--accent-2); }
.chip.c-md.zero { opacity: .45; }
.chip.c-md.zero b { color: var(--txt-dim); }
/* 选中的型号：亮青实底高亮，和普通型号拉开对比 */
.chip.c-md.on {
  color: #ffffff; font-weight: 700; letter-spacing: .4px; z-index: 2;
  border-color: var(--accent-2); background: color-mix(in srgb, #00aeff 45%, var(--panel));
}
.chip.c-md.on b { color: #ffffff; }
.hint { font-size: 10.5px; color: var(--txt-dim); white-space: nowrap; margin-right: 2px; }
.hint.on { color: var(--accent-2); }
/* 组合条件一个都没命中：提示转黄，避免看起来像"页面坏了" */
.hint.warn { color: var(--warn); }

/* 下钻单元：同一列、上下紧挨的 2 张桌子（悬停提示是哪两桌，点击高亮后进入两桌详情） */
.board-body { flex: 1; min-height: 0; display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
.pair-row { flex: 1; min-height: 0; display: flex; align-items: stretch; gap: 8px; }
.pair {
  flex: 1 1 0; min-width: 0;
  display: flex; flex-direction: column; gap: 4px;
  padding: 3px;
  border: 1px solid transparent; border-radius: 7px;
  cursor: pointer;
  transition: border-color .18s, background .18s, box-shadow .18s;
}
/* 悬停：先让用户看清这两桌是一个单元 */
.pair.hot { border-color: rgba(0, 174, 255, .5); background: color-mix(in srgb, #00aeff 12%, var(--panel)); }
.pair.hot .table { border-color: rgba(0, 174, 255, .45); }
/* 点击：两张桌子同时点亮，随后进入它们的详情页 */
.pair.pending {
  border-color: var(--accent-2);
  background: color-mix(in srgb, #00aeff 24%, var(--panel));
  box-shadow: 0 0 0 1px var(--accent-2), 0 0 18px rgba(0, 174, 255, .35);
}
.pair.pending .table {
  border-color: var(--accent-2);
  background: color-mix(in srgb, #00aeff 34%, var(--card));
}

/* 桌子：单元内上下各一张，悬停 / 选中反馈统一挂在 .pair 上 */
.table {
  flex: 1 1 0; min-height: 0;
  display: flex; align-items: center; gap: 6px;
  padding: 2px 6px;
  border: 1px solid var(--line); border-radius: 5px;
  /* 纯色实底：与详情页的桌子卡片同一套深蓝 */
  background: var(--card);
  transition: border-color .15s, background .15s;
}
/* 行号列：每一横排最左侧标行号（A.01 / A.02），内部两格高度与上下两张桌子一一对应 */
.row-axis {
  flex: 0 0 auto; width: 38px;
  display: flex; flex-direction: column; gap: 4px; padding: 3px 0;
}
.row-axis span {
  flex: 1 1 0; min-height: 0;
  display: flex; align-items: center; justify-content: flex-start;
  font-family: var(--font-num); font-size: 10px; letter-spacing: .3px; color: var(--txt-dim);
  white-space: nowrap;
}
/* 桌子内的工位：等宽均分、占满整桌（6 工位 6 等分，第 7-10 行的 2 工位 2 等分） */
.t-stations { flex: 1; min-width: 0; height: 100%; display: flex; gap: 4px; align-items: stretch; }
.t-stations .ws { flex: 1 1 0; min-width: 0; }

/* ---------- 工位小图标（只显示在制产品名，装不下则滚动） ----------
   与详情页 .d-ws 完全同款：状态色直接混进深蓝卡底（纯色实底），border 用状态色，
   --sc 一换，底色 / 边框 / 顶部色条一起变；悬停、筛选命中只是把混色比例提高 */
.ws {
  --sc: var(--ok);
  position: relative; overflow: hidden;
  flex: 1 1 0; min-width: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px;
  padding: 1px 3px;
  border: 1px solid var(--line); border-radius: 4px;
  background: color-mix(in srgb, var(--sc) 60%, var(--card));
  transition: border-color .15s, background .15s, transform .15s;
}
/* 工位悬停高亮（状态色比例提高，亮一档） */
.ws:hover {
  z-index: 3;
  transform: translateY(-1px) scale(1.06);
  border-color: var(--sc);
  background: color-mix(in srgb, var(--sc) 60%, var(--card));
}
/* 顶部状态色条 */
.ws::before {
  content: ""; position: absolute; left: 0; right: 0; top: 0; height: 2px;
  background: var(--sc);
}
.ws.st-normal { --sc: var(--ok); border-color: rgba(29, 209, 161, .7); }
.ws.st-warning { --sc: var(--warn); border-color: rgba(254, 202, 87, .8); }
.ws.st-error {
  --sc: var(--err); border-color: var(--err);
  /* 异常卡红色占比最高：远看就是一块红 */
  background: color-mix(in srgb, var(--err) 48%, var(--card));
}
.ws.st-idle {
  --sc: var(--idle); border-style: dashed; border-color: rgba(142, 181, 201, .55);
  /* 空闲卡彩度最低：灰蓝实底，和作业中的彩色卡片一眼区分 */
  background: color-mix(in srgb, #8eb5c9 18%, var(--card));
}

/* ---------- 型号筛选：命中工位常态高亮（沿用自身状态色），未命中置灰 ---------- */
.ws.matched {
  z-index: 2;
  border-color: var(--sc);
  background: color-mix(in srgb, var(--sc) 60%, var(--card));
  box-shadow: 0 0 0 1px var(--sc);
}

/* 未命中：置灰（悬停时恢复，便于查看） */
.ws.dim { opacity: .32; filter: grayscale(1); }
.ws.dim::before { opacity: .3; }
.ws.dim:hover { opacity: .95; filter: none; }

/* 卡内文字 = 在制产品名称（不再显示工位编号）：最多两行，装不下再滚动 */
.w-name {
  width: 100%; max-width: 100%;
  /* 限高两行（10px × 1.1 行高 ≈ 11px/行）：卡片够高就显示两行，只有一行时自动垂直居中 */
  max-height: 22px; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
}
.w-name-in { width: 100%; }
.w-seg {
  display: block;
  /* 字号字重沿用原来产品名那一行；颜色纯白：彩色卡底上对比最强 */
  font-size: 10px; font-weight: 500; letter-spacing: .2px; line-height: 11px;
  color: #ffffff; text-align: center;
  /* 长型号没有空格：允许在任意字符处折行，尽量在两行内放下 */
  word-break: break-all; white-space: normal;
}
/* 不滚动：只显示第一份（居中） */
.w-name .w-seg + .w-seg { display: none; }
/* 两行装不下：顶对齐显示前两行，滚动时两份首尾相接 */
.w-name.can-scroll { align-items: flex-start; }
.w-name.can-scroll .w-seg { text-align: left; }
.w-name.can-scroll .w-seg + .w-seg { display: block; }
/* 开页：滚动一次就停 */
.w-name.can-scroll.is-play .w-name-in { animation: wsMarqueeUpOnce var(--dur, 4s) linear 1; }
/* 鼠标移到这一组：这组工位循环滚动（从下往上单向） */
.w-name.can-scroll.is-loop .w-name-in { animation: wsMarqueeUpLoop var(--dur, 4s) linear infinite; }
/* 从下往上：内容向上平移一份的高度，两份相接所以看不出接缝
   两条 keyframes 内容相同、名字不同，切换播放状态时动画才会从头重播 */
@keyframes wsMarqueeUpOnce {
  from { transform: translateY(0); }
  to { transform: translateY(-50%); }
}
@keyframes wsMarqueeUpLoop {
  from { transform: translateY(0); }
  to { transform: translateY(-50%); }
}
@keyframes wsSubBlink { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }

/* ---------- 工位悬浮信息卡 ---------- */
.ws-tip {
  --sc: var(--ok);
  position: fixed; z-index: 999; pointer-events: none;
  width: 232px; padding: 9px 11px 10px;
  border: 1px solid rgba(0, 174, 255, .45); border-radius: 8px;
  background: var(--panel);
  box-shadow: 0 14px 36px rgba(0, 0, 0, .5);
  animation: wsTipIn .12s ease-out;
}
@keyframes wsTipIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }

.tip-head { display: flex; align-items: center; gap: 6px; padding-bottom: 7px; margin-bottom: 7px; border-bottom: 1px solid var(--line-soft); }
.tip-dot { width: 7px; height: 7px; border-radius: 50%; flex: 0 0 auto; background: var(--sc); }
.tip-head b { font-family: var(--font-num); font-size: 13px; letter-spacing: .5px; color: #ffffff; }
.tip-head em { margin-left: auto; font-style: normal; font-size: 10.5px; color: var(--sc); }
.tip-rows { display: flex; flex-direction: column; gap: 4px; }
.tip-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; font-size: 11.5px; }
.tip-row span { color: var(--txt-dim); white-space: nowrap; }
.tip-row b { font-family: var(--font-num); font-weight: 600; color: #ffffff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tip-note {
  margin-top: 7px; padding: 5px 8px; border-radius: 5px;
  font-size: 11px; color: var(--sc);
  background: color-mix(in srgb, var(--sc) 18%, var(--panel)); border-left: 2px solid var(--sc);
}
.ws-tip.tip-normal { --sc: var(--ok); }
.ws-tip.tip-warning { --sc: var(--warn); border-color: rgba(254, 202, 87, .5); }
.ws-tip.tip-error { --sc: var(--err); border-color: rgba(255, 107, 107, .55); }
.ws-tip.tip-idle { --sc: var(--idle); border-color: rgba(142, 181, 201, .45); }

/* ---------- 底部告警滚动条 ---------- */
.ticker { flex: 0 0 30px; display: flex; align-items: center; gap: 12px; padding: 0 14px; }
.ticker-label { display: flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; color: #ffffff; white-space: nowrap; }
.ticker-label i { width: 6px; height: 6px; border-radius: 50%; background: var(--err); animation: wsSubBlink 1.2s infinite; }
.ticker-viewport {
  flex: 1; overflow: hidden;
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent);
          mask-image: linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent);
}
.ticker-track { display: inline-flex; white-space: nowrap; will-change: transform; animation: gasTickerMove 40s linear infinite; }
@keyframes gasTickerMove { to { transform: translateX(-50%); } }
/* background 兜底：ProcessDesign.vue 的全局 .error 会命中本页 tk.error，需显式声明透明底 */
.tk { font-size: 11px; color: #cdddf7; margin-right: 52px; display: inline-flex; align-items: center; white-space: nowrap; background: transparent; }
.tk b { font-family: var(--font-num); font-weight: 400; color: var(--txt-dim); margin-right: 8px; }
.tk::before { content: "●"; font-size: 9px; margin-right: 7px; }
.tk.error { color: #ffb3b3; } .tk.error::before { color: var(--err); }
.tk.warning { color: #ffe08a; } .tk.warning::before { color: var(--warn); }
.tk.info { color: #8ee6ff; } .tk.info::before { color: var(--accent); }
.tk.done { color: #7bf0c0; } .tk.done::before { color: var(--ok); }
</style>
